/**
 * WabaCallWidget.js
 *
 * A globally-mounted floating WebRTC call widget that persists during navigation.
 * Handles:
 *  - Inbound calls (triggered via socket "inboundCallReceived" event)
 *  - Outbound calls (initiated from the inbox via window.wabaCallStart event)
 *  - WebRTC SDP signaling via /api/v1/whatsapp/calls
 *  - Call controls: accept, reject, mute, hang up
 *  - Minimise/expand — stays on screen while navigating
 */

import React, { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "next-i18next";
import { socket } from "../socket";

// Ringtone path in public folder
const RINGTONE_PATH = "/sounds/ring.wav";
const CALLING_SOUND_PATH = "/sounds/calling.mp3";

// Pre-load and cache audio elements to avoid autoplay restrictions
// These will be "unlocked" on first user interaction
let preloadedRingtone = null;
let preloadedCallingSound = null;
let audioUnlocked = false;

let audioUnlockPromise = null;

// Unlock HTML audio only from an actual user activation. Web Audio contexts are
// intentionally not created here because browsers reject them before a gesture.
const initializeAudio = async () => {
  if (audioUnlocked) return true;
  if (audioUnlockPromise) return audioUnlockPromise;

  if (!preloadedRingtone) {
    preloadedRingtone = new Audio(RINGTONE_PATH);
    preloadedRingtone.loop = true;
    preloadedRingtone.volume = 1.0;
    preloadedRingtone.load();
  }

  if (!preloadedCallingSound) {
    preloadedCallingSound = new Audio(CALLING_SOUND_PATH);
    preloadedCallingSound.loop = true;
    preloadedCallingSound.volume = 0.7;
    preloadedCallingSound.load();
  }

  audioUnlockPromise = Promise.all([
    preloadedRingtone.play(),
    preloadedCallingSound.play(),
  ])
    .then(() => {
      [preloadedRingtone, preloadedCallingSound].forEach((audio) => {
        audio.pause();
        audio.currentTime = 0;
      });
      audioUnlocked = true;
      return true;
    })
    .catch(() => false)
    .finally(() => {
      audioUnlockPromise = null;
    });

  return audioUnlockPromise;
};

const CALL_STATES = {
  IDLE: "IDLE",
  INCOMING: "INCOMING",
  CALLING: "CALLING",
  ACTIVE: "ACTIVE",
  ENDED: "ENDED",
};

const formatDuration = (seconds) => {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const s = (seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

export default function WabaCallWidget() {
  const { t } = useTranslation("common");

  const [callState, setCallState] = useState(CALL_STATES.IDLE);
  const [minimised, setMinimised] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeakerMuted, setIsSpeakerMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [callInfo, setCallInfo] = useState(null);
  const [socketReady, setSocketReady] = useState(false);
  const [audioBlocked, setAudioBlocked] = useState(false);
  // { call_id, channel_id, conversation_id, contact_name, phone_number, sdp, sdp_type }

  const localStreamRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const callInfoRef = useRef(null);
  const callStateRef = useRef(callState);

  // Handle click on widget to unlock audio and retry a blocked ringtone.
  const handleWidgetClick = useCallback(async () => {
    const unlocked = audioUnlocked || (await initializeAudio());
    if (
      unlocked &&
      callStateRef.current === CALL_STATES.INCOMING &&
      window._wabaRingAudio
    ) {
      window._wabaRingAudio
        .play()
        .then(() => {
          setAudioBlocked(false);
        })
        .catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (audioUnlocked) return undefined;

    const interactionEvents = ["pointerdown", "touchend", "keydown"];
    const removeListeners = () => {
      interactionEvents.forEach((eventName) => {
        document.removeEventListener(eventName, handleFirstInteraction, true);
      });
    };
    const handleFirstInteraction = async () => {
      if (await initializeAudio()) removeListeners();
    };

    interactionEvents.forEach((eventName) => {
      document.addEventListener(eventName, handleFirstInteraction, {
        capture: true,
      });
    });

    return removeListeners;
  }, []);

  // Sync callInfo to ref so event handlers always have the latest
  useEffect(() => {
    callInfoRef.current = callInfo;
  }, [callInfo]);

  // Sync callState to ref for use in socket event handlers
  useEffect(() => {
    callStateRef.current = callState;
  }, [callState]);

  // ── Wait for socket to become available ────────────────────────────────
  useEffect(() => {
    // Check if socket is ready, poll until it is
    const checkSocket = () => {
      if (socket && socket.connected) {
        setSocketReady(true);
        return true;
      }
      return false;
    };

    if (checkSocket()) return;

    // Socket exists but not connected yet
    if (socket) {
      const onConnect = () => setSocketReady(true);
      socket.on("connect", onConnect);
      return () => socket.off("connect", onConnect);
    }

    // Socket doesn't exist yet, poll for it
    const interval = setInterval(() => {
      if (socket) {
        clearInterval(interval);
        if (socket.connected) {
          setSocketReady(true);
        } else {
          const onConnect = () => setSocketReady(true);
          socket.on("connect", onConnect);
        }
      }
    }, 100);

    return () => clearInterval(interval);
  }, []);

  // ── Timer ──────────────────────────────────────────────────────────────
  const startTimer = useCallback(() => {
    setCallDuration(0);
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    timerIntervalRef.current = setInterval(() => {
      setCallDuration((prev) => prev + 1);
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  }, []);

  // ── WebRTC ─────────────────────────────────────────────────────────────
  const createPeerConnection = useCallback(() => {
    const pc = new RTCPeerConnection({
      iceServers: [
        { urls: "stun:stun.l.google.com:19302" },
        { urls: "stun:stun1.l.google.com:19302" },
      ],
    });

    pc.onicecandidate = () => {
      // ICE gathering — no ICE trickle with WhatsApp API; SDP sent after gathering complete
    };

    pc.ontrack = (event) => {
      if (remoteAudioRef.current && event.streams?.[0]) {
        const audioEl = remoteAudioRef.current;

        // Disconnect any previous stream
        audioEl.srcObject = null;

        // Set new stream
        audioEl.srcObject = event.streams[0];

        // Configure audio element for reliable playback
        audioEl.volume = 1.0;
        audioEl.muted = false;

        // Attempt to play with retry logic for autoplay restrictions
        const playAudio = async () => {
          try {
            await audioEl.play();
          } catch (err) {
            console.warn("Audio play failed, will retry:", err);
            // Retry after a brief delay (browser might need user gesture)
            setTimeout(async () => {
              try {
                await audioEl.play();
                console.log("Remote audio playing on retry");
              } catch (retryErr) {
                console.error("Audio play retry failed:", retryErr);
              }
            }, 500);
          }
        };

        playAudio();
      }
    };

    pc.oniceconnectionstatechange = () => {
      if (
        pc.iceConnectionState === "disconnected" ||
        pc.iceConnectionState === "failed" ||
        pc.iceConnectionState === "closed"
      ) {
        handleHangUp(true);
      }
    };

    return pc;
  }, []);

  const getLocalStream = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: false,
    });
    localStreamRef.current = stream;
    return stream;
  }, []);

  const cleanupWebRTC = useCallback(() => {
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null;
    }
    stopTimer();
  }, [stopTimer]);

  // ── Outbound Call ──────────────────────────────────────────────────────
  const initiateOutboundCall = useCallback(
    async ({
      phone_number_id,
      to,
      contact_name,
      channel_id,
      conversation_id,
    }) => {
      try {
        setCallState(CALL_STATES.CALLING);
        setMinimised(false);
        setCallDuration(0);
        setIsMuted(false);
        setIsSpeakerMuted(false);

        const stream = await getLocalStream();
        const pc = createPeerConnection();
        peerConnectionRef.current = pc;

        stream.getTracks().forEach((track) => pc.addTrack(track, stream));

        const offer = await pc.createOffer({ offerToReceiveAudio: true });
        await pc.setLocalDescription(offer);

        // Wait for ICE gathering to complete
        await new Promise((resolve) => {
          if (pc.iceGatheringState === "complete") return resolve();
          pc.onicegatheringstatechange = () => {
            if (pc.iceGatheringState === "complete") resolve();
          };
          // Fallback timeout
          setTimeout(resolve, 3000);
        });

        const finalSdp = pc.localDescription?.sdp || offer.sdp;

        const resp = await fetch("/api/v1/whatsapp/calls", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            phone_number_id,
            to,
            action: "connect",
            session: { sdp_type: "offer", sdp: finalSdp },
          }),
        });

        const data = await resp.json();
        if (!resp.ok) throw new Error(data.error || "Failed to initiate call");

        const call_id = data.call_id;

        setCallInfo({
          call_id,
          channel_id,
          conversation_id,
          phone_number_id,
          contact_name: contact_name || to,
          phone_number: to,
          direction: "OUTBOUND",
        });

        // Play dialing sound
        try {
          if (window._wabaCallingAudio) {
            window._wabaCallingAudio.pause();
            window._wabaCallingAudio.currentTime = 0;
            window._wabaCallingAudio = null;
          }

          // Use preloaded audio if available, otherwise create new
          let callingSound;
          if (audioUnlocked && preloadedCallingSound) {
            callingSound = preloadedCallingSound;
            callingSound.currentTime = 0;
          } else {
            callingSound = new Audio(CALLING_SOUND_PATH);
            callingSound.loop = true;
            callingSound.volume = 0.7;
          }

          callingSound
            .play()
            .catch((err) =>
              console.warn("Calling sound autoplay blocked:", err),
            );

          window._wabaCallingAudio = callingSound;
        } catch (err) {
          console.error("Failed to create calling Audio:", err);
        }

        // Note: SDP answer will come via webhook socket event "callStatusUpdated"
      } catch (err) {
        console.error("WABA Call initiation error:", err);
        cleanupWebRTC();
        setCallState(CALL_STATES.IDLE);
      }
    },
    [createPeerConnection, getLocalStream, cleanupWebRTC],
  );

  // ── Accept Inbound Call ────────────────────────────────────────────────
  const acceptCall = useCallback(async () => {
    const info = callInfoRef.current;
    if (!info) return;

    try {
      setCallState(CALL_STATES.ACTIVE);

      const stream = await getLocalStream();
      const pc = createPeerConnection();
      peerConnectionRef.current = pc;

      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      // Set the remote SDP offer from the inbound call
      if (info.sdp) {
        await pc.setRemoteDescription(
          new RTCSessionDescription({
            type: info.sdp_type || "offer",
            sdp: info.sdp,
          }),
        );
      }

      // Create answer
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      // Send pre_accept with early SDP (before ICE gathering completes)
      // This signals intent to accept and provides initial SDP for early media
      const earlySdp = answer.sdp;
      await fetch("/api/v1/whatsapp/calls", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone_number_id: info.phone_number_id,
          action: "pre_accept",
          call_id: info.call_id,
          // session: { sdp_type: "answer", sdp: earlySdp },
        }),
      });

      // Wait for ICE gathering to complete
      await new Promise((resolve) => {
        if (pc.iceGatheringState === "complete") return resolve();
        pc.onicegatheringstatechange = () => {
          if (pc.iceGatheringState === "complete") resolve();
        };
        setTimeout(resolve, 3000);
      });

      const finalSdp = pc.localDescription?.sdp || answer.sdp;

      // Send accept with final SDP (after ICE gathering)
      await fetch("/api/v1/whatsapp/calls", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone_number_id: info.phone_number_id,
          action: "accept",
          call_id: info.call_id,
          session: { sdp_type: "answer", sdp: finalSdp },
        }),
      });

      // Ensure audio plays after user interaction (bypass autoplay restrictions)
      if (remoteAudioRef.current && remoteAudioRef.current.srcObject) {
        remoteAudioRef.current.play().catch(() => {});
      }

      startTimer();
    } catch (err) {
      console.error("WABA Call accept error:", err);
      cleanupWebRTC();
      setCallState(CALL_STATES.IDLE);
    }
  }, [createPeerConnection, getLocalStream, cleanupWebRTC, startTimer]);

  // ── Reject / Hang Up ───────────────────────────────────────────────────
  const handleHangUp = useCallback(
    async (fromRemote = false) => {
      const info = callInfoRef.current;
      const currentState = callStateRef.current;
      stopTimer();

      if (!fromRemote && info?.call_id && info?.phone_number_id) {
        try {
          const action =
            currentState === CALL_STATES.INCOMING ? "reject" : "terminate";

          await fetch("/api/v1/whatsapp/calls", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              phone_number_id: info.phone_number_id,
              action,
              call_id: info.call_id,
            }),
          });
        } catch (e) {
          console.error("WABA hang up error:", e);
        }
      }

      cleanupWebRTC();
      setCallState(CALL_STATES.ENDED);

      // After 2 seconds reset to IDLE
      setTimeout(() => {
        setCallState(CALL_STATES.IDLE);
        setCallInfo(null);
        setCallDuration(0);
        setMinimised(false);
      }, 2000);
    },
    [cleanupWebRTC, stopTimer],
  );

  // ── Mute ──────────────────────────────────────────────────────────────
  const toggleMute = useCallback(() => {
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach((track) => {
        track.enabled = !track.enabled;
      });
      setIsMuted((prev) => !prev);
    }
  }, []);

  const toggleSpeaker = useCallback(() => {
    if (remoteAudioRef.current) {
      remoteAudioRef.current.muted = !remoteAudioRef.current.muted;
      setIsSpeakerMuted((prev) => !prev);
    }
  }, []);

  // ── Join call rooms on mount ───────────────────────────────────────────
  useEffect(() => {
    if (!socketReady || !socket) return;

    // Join call rooms for all channels when socket is ready
    socket.emit("joinCallRooms");

    // Also rejoin on reconnect
    const onReconnect = () => {
      socket.emit("joinCallRooms");
    };
    socket.on("connect", onReconnect);

    return () => {
      socket.off("connect", onReconnect);
    };
  }, [socketReady]);

  // ── Socket Events ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!socketReady || !socket) return;

    const onInboundCall = (data) => {
      // data: { call_id, channel_id, conversation_id, contact_name, phone_number, sdp, sdp_type, phone_number_id }
      // Use ref to get current state to avoid stale closure
      if (callStateRef.current !== CALL_STATES.IDLE) return; // Already in a call

      setCallInfo({ ...data, direction: "INBOUND" });
      setCallState(CALL_STATES.INCOMING);
      setMinimised(false);

      // Play ring sound
      try {
        // Stop any existing ringtone first
        if (window._wabaRingAudio) {
          window._wabaRingAudio.pause();
          window._wabaRingAudio.currentTime = 0;
          window._wabaRingAudio = null;
        }

        // Use preloaded audio if available (already unlocked), otherwise create new
        let ring;
        if (audioUnlocked && preloadedRingtone) {
          ring = preloadedRingtone;
          ring.currentTime = 0;
        } else {
          ring = new Audio(RINGTONE_PATH);
          ring.loop = true;
          ring.volume = 1.0;
        }

        // Play with promise handling
        const playPromise = ring.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setAudioBlocked(false);
            })
            .catch((err) => {
              console.warn("Ringtone autoplay blocked:", err);
              setAudioBlocked(true);
              // Audio is retried by the widget's next user interaction.
              // Request notification permission as fallback
              if (
                "Notification" in window &&
                Notification.permission === "default"
              ) {
                Notification.requestPermission();
              }
            });
        }

        // Store ref so we can stop it
        window._wabaRingAudio = ring;
      } catch (err) {
        console.error("Failed to create ringtone Audio:", err);
      }
    };

    const onCallStatusUpdated = (data) => {
      const currentState = callStateRef.current;
      if (data.status === "ACCEPTED" && currentState === CALL_STATES.CALLING) {
        setCallState(CALL_STATES.ACTIVE);
        startTimer();
        // Handle incoming SDP answer if provided
        if (data.sdp && peerConnectionRef.current) {
          peerConnectionRef.current
            .setRemoteDescription(
              new RTCSessionDescription({
                type: data.sdp_type || "answer",
                sdp: data.sdp,
              }),
            )
            .then(() => {
              // Ensure audio plays after remote description is set
              if (remoteAudioRef.current && remoteAudioRef.current.srcObject) {
                remoteAudioRef.current.play().catch(() => {});
              }
            })
            .catch(console.error);
        }
      } else if (
        ["REJECTED", "BUSY", "UNAVAILABLE"].includes(data.status) &&
        currentState === CALL_STATES.CALLING
      ) {
        cleanupWebRTC();
        setCallState(CALL_STATES.ENDED);
        setTimeout(() => {
          setCallState(CALL_STATES.IDLE);
          setCallInfo(null);
        }, 2000);
      }
    };

    const onOutboundCallSdpAnswer = (data) => {
      // Handle SDP answer for outbound calls
      const currentState = callStateRef.current;
      if (
        currentState === CALL_STATES.CALLING &&
        data.sdp &&
        peerConnectionRef.current
      ) {
        peerConnectionRef.current
          .setRemoteDescription(
            new RTCSessionDescription({
              type: data.sdp_type || "answer",
              sdp: data.sdp,
            }),
          )
          .then(() => {
            setCallState(CALL_STATES.ACTIVE);
            startTimer();
            // Ensure audio plays
            if (remoteAudioRef.current && remoteAudioRef.current.srcObject) {
              remoteAudioRef.current.play().catch(() => {});
            }
          })
          .catch((err) => {
            console.error("Failed to set remote SDP:", err);
          });
      }
    };

    const onCallTerminated = () => {
      handleHangUp(true);
    };

    socket.on("inboundCallReceived", onInboundCall);
    socket.on("callStatusUpdated", onCallStatusUpdated);
    socket.on("outboundCallSdpAnswer", onOutboundCallSdpAnswer);
    socket.on("callTerminated", onCallTerminated);

    return () => {
      socket.off("inboundCallReceived", onInboundCall);
      socket.off("callStatusUpdated", onCallStatusUpdated);
      socket.off("outboundCallSdpAnswer", onOutboundCallSdpAnswer);
      socket.off("callTerminated", onCallTerminated);
    };
  }, [socketReady, startTimer, cleanupWebRTC, handleHangUp]);

  // ── Window event for outbound calls from inbox ─────────────────────────
  useEffect(() => {
    const handleOutbound = (e) => {
      initiateOutboundCall(e.detail);
    };
    window.addEventListener("wabaCallStart", handleOutbound);
    return () => window.removeEventListener("wabaCallStart", handleOutbound);
  }, [initiateOutboundCall]);

  // ── Stop ringtone when call state changes ──────────────────────────────
  useEffect(() => {
    // Stop incoming ringtone when no longer incoming
    if (callState !== CALL_STATES.INCOMING && window._wabaRingAudio) {
      window._wabaRingAudio.pause();
      window._wabaRingAudio.currentTime = 0;
      window._wabaRingAudio = null;
    }

    // Reset audio blocked indicator when not in incoming state
    if (callState !== CALL_STATES.INCOMING) {
      setAudioBlocked(false);
    }

    // Stop calling/dialing sound when no longer calling (connected or ended)
    if (callState !== CALL_STATES.CALLING && window._wabaCallingAudio) {
      window._wabaCallingAudio.pause();
      window._wabaCallingAudio.currentTime = 0;
      window._wabaCallingAudio = null;
    }
  }, [callState]);

  // ── Cleanup on unmount ─────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      cleanupWebRTC();
      // Also stop any playing sounds
      if (window._wabaRingAudio) {
        window._wabaRingAudio.pause();
        window._wabaRingAudio = null;
      }
      if (window._wabaCallingAudio) {
        window._wabaCallingAudio.pause();
        window._wabaCallingAudio = null;
      }
    };
  }, [cleanupWebRTC]);

  // ── Render ─────────────────────────────────────────────────────────────
  const contactName =
    callInfo?.contact_name ||
    callInfo?.phone_number ||
    t("calling.unknown-caller");
  const isActive = callState === CALL_STATES.ACTIVE;
  const isIncoming = callState === CALL_STATES.INCOMING;
  const isCalling = callState === CALL_STATES.CALLING;
  const isEnded = callState === CALL_STATES.ENDED;
  const isIdle = callState === CALL_STATES.IDLE;

  return (
    <>
      {/* Hidden audio element for remote audio - MUST always be in DOM for WebRTC */}
      <audio
        ref={remoteAudioRef}
        autoPlay
        playsInline
        style={{ display: "none" }}
      />

      {/* Don't render widget UI when idle */}
      {isIdle ? null : (
        <>
          {/* Floating Call Widget */}
          <div
            id="waba-call-widget"
            onClick={handleWidgetClick}
            style={{
              position: "fixed",
              bottom: minimised ? "16px" : "24px",
              right: "24px",
              zIndex: 9999,
              width: minimised ? "220px" : "300px",
              background:
                "linear-gradient(135deg, #0a4a2b 0%, #065f46 60%, #047857 100%)",
              borderRadius: "16px",
              boxShadow:
                "0 20px 60px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.1) inset",
              color: "#fff",
              overflow: "hidden",
              transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
              userSelect: "none",
            }}
          >
            {/* Animated background circles */}
            {(isIncoming || isCalling) && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  overflow: "hidden",
                  borderRadius: "16px",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: "-20px",
                    right: "-20px",
                    width: "120px",
                    height: "120px",
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.05)",
                    animation: "wabaCallPulse 2s ease-in-out infinite",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    bottom: "-30px",
                    left: "-20px",
                    width: "160px",
                    height: "160px",
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.03)",
                    animation: "wabaCallPulse 2.5s ease-in-out infinite 0.5s",
                  }}
                />
              </div>
            )}

            {/* Header bar */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 14px 8px",
                borderBottom: "1px solid rgba(255,255,255,0.1)",
                position: "relative",
                zIndex: 1,
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                {/* Pulse dot */}
                <div
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    background: isActive
                      ? "#34d399"
                      : isIncoming
                        ? "#fbbf24"
                        : isCalling
                          ? "#60a5fa"
                          : "#ef4444",
                    boxShadow: `0 0 6px ${isActive ? "#34d399" : isIncoming ? "#fbbf24" : isCalling ? "#60a5fa" : "#ef4444"}`,
                    animation:
                      isActive || isIncoming || isCalling
                        ? "wabaCallDot 1.5s ease-in-out infinite"
                        : "none",
                  }}
                />
                <span
                  style={{
                    fontSize: "11px",
                    opacity: 0.8,
                    fontWeight: 500,
                    letterSpacing: "0.02em",
                  }}
                >
                  {isIncoming && t("calling.incoming")}
                  {isCalling && t("calling.calling")}
                  {isActive && formatDuration(callDuration)}
                  {isEnded && t("calling.call-ended")}
                </span>
              </div>
              <div style={{ display: "flex", gap: "6px" }}>
                <button
                  onClick={() => setMinimised((p) => !p)}
                  style={{
                    background: "rgba(255,255,255,0.12)",
                    border: "none",
                    color: "#fff",
                    borderRadius: "50%",
                    width: "24px",
                    height: "24px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    transition: "background 0.2s",
                  }}
                  title={
                    minimised ? t("calling.expand") : t("calling.minimise")
                  }
                >
                  <i
                    className={`fas fa-chevron-${minimised ? "up" : "down"}`}
                  />
                </button>
              </div>
            </div>

            {/* Body */}
            {!minimised && (
              <div
                style={{
                  padding: "16px 14px 18px",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                {/* Avatar + Contact */}
                <div style={{ textAlign: "center", marginBottom: "16px" }}>
                  <div
                    style={{
                      width: "56px",
                      height: "56px",
                      borderRadius: "50%",
                      background: "rgba(255,255,255,0.15)",
                      margin: "0 auto 10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "24px",
                      border: "2px solid rgba(255,255,255,0.2)",
                      position: "relative",
                    }}
                  >
                    <i className="fas fa-user" />
                    {(isIncoming || isCalling) && (
                      <div
                        style={{
                          position: "absolute",
                          inset: "-4px",
                          borderRadius: "50%",
                          border: "2px solid rgba(255,255,255,0.3)",
                          animation: "wabaCallRing 1.8s ease-out infinite",
                        }}
                      />
                    )}
                  </div>
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: "15px",
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {contactName}
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      opacity: 0.65,
                      marginTop: "2px",
                    }}
                  >
                    {callInfo?.phone_number}
                  </div>
                  {callInfo?.direction && (
                    <div
                      style={{
                        fontSize: "11px",
                        opacity: 0.5,
                        marginTop: "2px",
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                      }}
                    >
                      {callInfo.direction === "INBOUND"
                        ? "↙ " + t("calling.inbound")
                        : "↗ " + t("calling.outbound")}
                    </div>
                  )}
                </div>

                {/* INCOMING state — Accept / Reject */}
                {isIncoming && (
                  <>
                    {/* Audio blocked indicator */}
                    {audioBlocked && (
                      <div
                        style={{
                          textAlign: "center",
                          marginBottom: "12px",
                          padding: "8px 12px",
                          background: "rgba(251, 191, 36, 0.2)",
                          borderRadius: "8px",
                          fontSize: "12px",
                          color: "#fbbf24",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "6px",
                          cursor: "pointer",
                          animation: "wabaCallPulse 1.5s ease-in-out infinite",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleWidgetClick();
                        }}
                      >
                        <i className="fas fa-volume-mute" />
                        <span>{t("calling.click-to-enable-sound")}</span>
                      </div>
                    )}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        gap: "24px",
                      }}
                    >
                      <button
                        onClick={() => handleHangUp(false)}
                        style={{
                          width: "52px",
                          height: "52px",
                          borderRadius: "50%",
                          background: "#ef4444",
                          border: "none",
                          color: "#fff",
                          cursor: "pointer",
                          fontSize: "20px",
                          boxShadow: "0 4px 15px rgba(239,68,68,0.4)",
                          transition: "transform 0.15s, box-shadow 0.15s",
                        }}
                        title={t("calling.reject")}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "scale(1.1)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      >
                        <i className="fas fa-phone-slash" />
                      </button>
                      <button
                        onClick={acceptCall}
                        style={{
                          width: "52px",
                          height: "52px",
                          borderRadius: "50%",
                          background: "#10b981",
                          border: "none",
                          color: "#fff",
                          cursor: "pointer",
                          fontSize: "20px",
                          boxShadow: "0 4px 15px rgba(16,185,129,0.4)",
                          transition: "transform 0.15s, box-shadow 0.15s",
                          animation: "wabaCallPulse 1.5s ease-in-out infinite",
                        }}
                        title={t("calling.accept")}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "scale(1.1)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      >
                        <i className="fas fa-phone" />
                      </button>
                    </div>
                  </>
                )}

                {/* CALLING state — cancel button */}
                {isCalling && (
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <button
                      onClick={() => handleHangUp(false)}
                      style={{
                        width: "52px",
                        height: "52px",
                        borderRadius: "50%",
                        background: "#ef4444",
                        border: "none",
                        color: "#fff",
                        cursor: "pointer",
                        fontSize: "20px",
                        boxShadow: "0 4px 15px rgba(239,68,68,0.4)",
                      }}
                      title={t("calling.cancel")}
                    >
                      <i className="fas fa-phone-slash" />
                    </button>
                  </div>
                )}

                {/* ACTIVE state — mute / speaker / hang up */}
                {isActive && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      gap: "16px",
                    }}
                  >
                    <button
                      onClick={toggleMute}
                      style={{
                        width: "42px",
                        height: "42px",
                        borderRadius: "50%",
                        background: isMuted
                          ? "#ef4444"
                          : "rgba(255,255,255,0.15)",
                        border: "1px solid rgba(255,255,255,0.2)",
                        color: "#fff",
                        cursor: "pointer",
                        fontSize: "16px",
                        transition: "background 0.2s",
                      }}
                      title={isMuted ? t("calling.unmute") : t("calling.mute")}
                    >
                      <i
                        className={`fas fa-microphone${isMuted ? "-slash" : ""}`}
                      />
                    </button>
                    <button
                      onClick={() => handleHangUp(false)}
                      style={{
                        width: "52px",
                        height: "52px",
                        borderRadius: "50%",
                        background: "#ef4444",
                        border: "none",
                        color: "#fff",
                        cursor: "pointer",
                        fontSize: "20px",
                        boxShadow: "0 4px 15px rgba(239,68,68,0.4)",
                      }}
                      title={t("calling.hang-up")}
                    >
                      <i className="fas fa-phone-slash" />
                    </button>
                    <button
                      onClick={toggleSpeaker}
                      style={{
                        width: "42px",
                        height: "42px",
                        borderRadius: "50%",
                        background: isSpeakerMuted
                          ? "#ef4444"
                          : "rgba(255,255,255,0.15)",
                        border: "1px solid rgba(255,255,255,0.2)",
                        color: "#fff",
                        cursor: "pointer",
                        fontSize: "16px",
                        transition: "background 0.2s",
                      }}
                      title={
                        isSpeakerMuted
                          ? t("calling.speaker-on")
                          : t("calling.speaker-off")
                      }
                    >
                      <i
                        className={`fas fa-volume-${isSpeakerMuted ? "mute" : "up"}`}
                      />
                    </button>
                  </div>
                )}

                {/* ENDED state */}
                {isEnded && (
                  <div
                    style={{
                      textAlign: "center",
                      opacity: 0.7,
                      fontSize: "13px",
                    }}
                  >
                    <i className="fas fa-phone-slash me-2" />
                    {t("calling.call-ended")}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* CSS Animations */}
          <style jsx global>{`
            @keyframes wabaCallPulse {
              0%,
              100% {
                transform: scale(1);
                opacity: 1;
              }
              50% {
                transform: scale(1.05);
                opacity: 0.85;
              }
            }
            @keyframes wabaCallRing {
              0% {
                transform: scale(1);
                opacity: 0.8;
              }
              100% {
                transform: scale(1.5);
                opacity: 0;
              }
            }
            @keyframes wabaCallDot {
              0%,
              100% {
                opacity: 1;
              }
              50% {
                opacity: 0.4;
              }
            }
          `}</style>
        </>
      )}
    </>
  );
}
